// "AI with your own key" settings (M15). The key is stored only in this browser (IndexedDB 'epb-ai'), is
// never logged, and is never written into sidecars, designs or exports. Without a key every AI button is
// hidden and the app makes no network call (tested).
import { create } from 'zustand';
import { createStore, del, get, set, type UseStore } from 'idb-keyval';

export type AiProvider = 'openai' | 'google';
export type AiModels = { vision: string; image: string };
export type AiSettings = { provider: AiProvider; key: string; models: Record<AiProvider, AiModels> };

/** Defaults (editable in the AI screen, since providers rename models). */
export const DEFAULT_MODELS: Record<AiProvider, AiModels> = {
  openai: { vision: 'gpt-4.1-mini', image: 'gpt-image-1' },
  google: { vision: 'gemini-2.5-flash', image: 'gemini-2.5-flash-image' },
};

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-ai', 'ai'));
const KEY = 'settings';

type AiState = { settings: AiSettings | null; loaded: boolean };
export const useAi = create<AiState>(() => ({ settings: null, loaded: false }));

/** True when a key is set (the AI buttons show). */
export const aiReady = () => !!useAi.getState().settings?.key;

export async function loadAi(): Promise<AiSettings | null> {
  if (useAi.getState().loaded) return useAi.getState().settings;
  const s = (await get<AiSettings>(KEY, store()).catch(() => undefined)) ?? null;
  const settings = s && { ...s, models: { ...DEFAULT_MODELS, ...s.models } };
  useAi.setState({ settings, loaded: true });
  return settings;
}

export async function saveAi(s: AiSettings): Promise<void> {
  const clean = { ...s, key: s.key.trim() };
  useAi.setState({ settings: clean.key ? clean : null, loaded: true });
  // Storage can be unavailable (private window): the key then lasts for this session only.
  if (clean.key) await set(KEY, clean, store()).catch(() => undefined);
  else await del(KEY, store()).catch(() => undefined);
}

/** "Remove key": forgets it in this browser. */
export async function removeAiKey(): Promise<void> {
  useAi.setState({ settings: null, loaded: true });
  await del(KEY, store()).catch(() => undefined);
}

/** A key shown on screen: first 3 and last 4 characters only. */
export const maskKey = (k: string) => (k.length <= 10 ? '••••' : `${k.slice(0, 3)}…${k.slice(-4)}`);
